// Profile tab — App Flow §6: personal info, settings, push, text size, logout, delete.
import {
  Button,
  Card,
  ConfirmDialog,
  DatePicker,
  Input,
  PageHeader,
  Section,
  Switch,
  useToast,
} from '@roots/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { InstallCard } from '../components/install-card.js';
import { PushSettings } from '../components/push-card.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

export function ProfileScreen() {
  const { user, setUser, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [name, setName] = useState(user?.name ?? '');
  const [birthday, setBirthday] = useState(user?.birthday ?? '');
  const [city, setCity] = useState(user?.city ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteWord, setDeleteWord] = useState('');

  const { data: prefs } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.notificationPreferences(),
  });

  const save = useMutation({
    mutationFn: () => api.updateMe({ name, birthday: birthday || null, city: city || null }),
    onSuccess: (updated) => {
      setUser(updated as never);
      push('Saved.');
    },
  });

  const setTextSize = useMutation({
    mutationFn: (size: 'default' | 'large') => api.updateMe({ text_size_pref: size }),
    onSuccess: (updated) => setUser(updated as never),
  });

  const setPref = useMutation({
    mutationFn: (payload: { type: string; enabled: boolean }) =>
      api.updateNotificationPreferences({ preferences: [payload] }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-preferences'] }),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteMe(),
    onSuccess: async () => {
      await signOut();
      window.location.replace('/');
    },
    onError: () => push('We couldn’t process your request right now. Try again later.', 'error'),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Profile" />

      <Section title="Personal info">
        <Card className="flex flex-col gap-3">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <DatePicker
            label="Birthday"
            value={birthday || null}
            onChange={(iso) => setBirthday(iso ?? '')}
          />
          {!birthday && (
            <p className="type-caption">Add your birthday so your family can celebrate you!</p>
          )}
          <Input
            label="City (optional)"
            value={city ?? ''}
            onChange={(e) => setCity(e.target.value)}
            hint="Helps Roots pick themes and destinations closer to home."
          />
          <p className="type-caption">Signed in as {user?.email}</p>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </Card>
      </Section>

      <Section title="My contributions">
        <Button variant="secondary" onClick={() => void navigate({ to: '/profile/contributions' })}>
          See everything I’ve shared
        </Button>
      </Section>

      <Section title="Settings">
        <Card className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <span className="type-body">Text size</span>
            <div className="flex gap-2">
              {(['default', 'large'] as const).map((size) => (
                <Button
                  key={size}
                  variant={user?.text_size_pref === size ? 'primary' : 'ghost'}
                  onClick={() => {
                    document.documentElement.dataset.textSize = size;
                    localStorage.setItem('roots:text-size', size);
                    setTextSize.mutate(size);
                  }}
                >
                  {size === 'default' ? 'Default' : 'Large'}
                </Button>
              ))}
            </div>
          </div>

          <PushSettings />

          <InstallCard />

          <div className="flex flex-col gap-2">
            <p className="type-subheading">What we let you know about</p>
            {prefs?.preferences.map((pref) => (
              <Switch
                key={pref.type}
                label={prefLabel(pref.type)}
                checked={pref.enabled}
                disabled={pref.system_critical}
                onCheckedChange={(enabled) => setPref.mutate({ type: pref.type, enabled })}
              />
            ))}
          </div>

          <Button variant="ghost" onClick={() => void signOut()}>
            Log out
          </Button>
          <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
            Delete account
          </Button>
        </Card>
      </Section>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete your account?"
        description="Your personal data will be deleted. Your contributions in published editions will be preserved anonymously."
        destructive
        confirmLabel="Delete"
        disabled={deleteWord !== 'DELETE'}
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      >
        <Input
          label="Type DELETE to confirm"
          value={deleteWord}
          onChange={(e) => setDeleteWord(e.target.value)}
        />
      </ConfirmDialog>
    </div>
  );
}

function prefLabel(type: string) {
  const text = type.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}
